/**
 * Permission codes the Consolidation screens declare an interest in.
 *
 * These are the dot-strings `internal/consol/permissions.go` registers with the
 * RBAC catalogue (CLAUDE.md §6). The package splits them six ways and the
 * screens honour the split rather than collapsing it: maintaining **who is in
 * the group** is a structural act, **generating a run** is a significant
 * compute-and-persist action, **deciding an elimination** is a human's
 * judgement call the run only proposes, and **taking a copy away** is audited
 * separately because a copy of a report is still the report.
 *
 * Reading is its own permission throughout, which is what lets an Auditor see
 * every figure in a consolidation without being able to change one.
 *
 * `org.company.read` belongs to another module and is listed because these
 * screens cannot do their job without it: a group names a parent company, a
 * member names a subsidiary, and every consolidated line is one company's. The
 * API returns company **codes** on most DTOs but not names, and never on the
 * group list, so the screens read `internal/org`'s own list to label them.
 * Where that permission is absent the code is shown on its own — never a
 * fabricated name.
 *
 * `gl.report.read` is likewise another module's: the trial balance drills
 * through to the GL ledger behind a translated figure, and a user who cannot
 * open the ledger gets no link rather than a link that 403s.
 */

export const CONSOL_PERMISSIONS = {
  groupRead: 'consol.group.read',
  groupManage: 'consol.group.manage',

  runRead: 'consol.run.read',
  runGenerate: 'consol.run.generate',
  /** Publish a run (locking it) and restate a published one (FSD 2.2.7 Publish). */
  runPublish: 'consol.run.publish',

  eliminationDecide: 'consol.elimination.decide',

  reportExport: 'consol.report.export',

  /** Read a group's readiness for a fiscal period (FSD 2.2.7 V01, V02). */
  readinessRead: 'consol.readiness.read',
  /** Read the local-to-group account map and its version history. */
  mappingRead: 'consol.mapping.read',
  /** Change the account map and ask for suggestions. */
  mappingManage: 'consol.mapping.manage',

  /* ------------------------------------------------- other modules' codes */

  companyRead: 'org.company.read',
  glReportRead: 'gl.report.read',
  /** Fiscal periods: the reporting period of a readiness check and of a run. */
  fiscalRead: 'org.fiscal.read',
  /** A member's local chart, listed on the account map. */
  accountRead: 'org.account.read',
  /** The Part 1 charts of accounts a group chart is chosen from, and their segments. */
  coaStructureRead: 'coa.structure.read',
  /** The group chart's natural-account values: the group accounts. */
  coaValueSetRead: 'coa.value_set.read',
} as const

export type ConsolPermission = (typeof CONSOL_PERMISSIONS)[keyof typeof CONSOL_PERMISSIONS]
